import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import JerseyConfigurator from "../components/JerseyConfigurator";
import { toast } from "sonner";
import ClubHeader, { COLOR_HEX } from "../components/club/ClubHeader";
import SponsorshipForm from "../components/club/SponsorshipForm";
import CurrentSponsorsList from "../components/club/CurrentSponsorsList";

export default function ClubDetail() {
    const { id } = useParams();
    const { user } = useAuth();

    const [data, setData] = useState(null);
    const [prices, setPrices] = useState({});
    const [age, setAge] = useState("");
    const [selected, setSelected] = useState([]);
    const [, setLogoFile] = useState(null);
    const [logoPath, setLogoPath] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [sponsorships, setSponsorships] = useState([]);

    const canSeePrices = !!user && (user.role === "admin" || (user.role === "sponsor" && user.status === "approved"));
    const isPendingSponsor = !!user && user.role === "sponsor" && user.status !== "approved";

    useEffect(() => {
        (async () => {
            const { data: cdata } = await api.get(`/clubs/${id}`);
            setData(cdata);
            const firstCat = cdata.club.age_categories?.[0] || "Senior";
            setAge(firstCat);
            if (canSeePrices) {
                try {
                    const { data: pdata } = await api.get("/config/prices/secured");
                    setPrices(pdata.zone_prices);
                } catch {
                    // best-effort fetch, ignore errors
                }
            } else {
                setPrices({});
            }
        })().catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message));
        // eslint-disable-next-line
    }, [id, canSeePrices]);

    useEffect(() => {
        if (!data?.club || !age) return;
        api.get(`/clubs/${data.club.id}/jerseys/${encodeURIComponent(age)}/sponsorships`)
            .then((r) => setSponsorships(r.data.sponsorships || []))
            .catch(() => {});
    }, [data, age]);

    if (!data) return <div className="p-12 text-zinc-500">Chargement…</div>;
    const { club, jerseys } = data;
    const jersey = jerseys.find((j) => j.age_category === age) || { total_collected: 0 };
    const teamInfo = (club.teams_by_category || []).find((t) => t.age_category === age) || {};
    const numPlayers = (teamInfo.num_teams || 0) * (teamInfo.players_per_team || 0);

    const toggleZone = (k) => {
        setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
    };

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-12">
            <ClubHeader club={club} />

            <section id="configurator" className="grid grid-cols-1 lg:grid-cols-12 gap-8 scroll-mt-20">
                <div className="lg:col-span-7 card-tu">
                    <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                        <h2 className="font-display text-3xl uppercase tracking-tight">Configurez le maillot</h2>
                        <select
                            data-testid="age-category-select"
                            value={age}
                            onChange={(e) => { setAge(e.target.value); setSelected([]); }}
                            className="input-tu w-auto"
                        >
                            {(club.age_categories || []).map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>
                    {(club.colors && club.colors.length > 0) && (
                        <div className="mb-6 border border-zinc-800 bg-zinc-950 p-4" data-testid="team-colors">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-3">
                                Les couleurs de l'équipe
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                {club.colors.map((c) => (
                                    <div key={c} className="flex items-center gap-2">
                                        <span
                                            className="w-7 h-7 rounded-full border-2 border-zinc-700 shrink-0"
                                            style={{ background: COLOR_HEX[c] || "#a1a1aa" }}
                                            aria-label={c}
                                        />
                                        <span className="text-xs uppercase tracking-widest text-zinc-300">{c}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <JerseyConfigurator
                        prices={prices}
                        selected={selected}
                        onToggle={canSeePrices ? toggleZone : undefined}
                        sponsorships={sponsorships.map((s) => ({ ...s, jersey_status: jersey.status }))}
                        showPrices={canSeePrices}
                        selectable={canSeePrices}
                        previewLogo={logoPreview}
                        majorColor={
                            club.colors && club.colors[0]
                                ? { name: club.colors[0], hex: COLOR_HEX[club.colors[0]] || "#a1a1aa" }
                                : null
                        }
                    />
                    <p className="text-center text-xs text-zinc-500 mt-4 uppercase tracking-widest">
                        {canSeePrices
                            ? "Cliquez sur les emplacements pour les sélectionner"
                            : (isPendingSponsor
                                ? "Votre compte sponsor est en attente de validation par l'admin"
                                : "Connectez-vous en tant que sponsor validé pour sponsoriser")}
                    </p>
                </div>

                <div className="lg:col-span-5 space-y-6">
                    <SponsorshipForm
                        canSeePrices={canSeePrices}
                        isPendingSponsor={isPendingSponsor}
                        user={user}
                        club={club}
                        age={age}
                        selected={selected}
                        prices={prices}
                        numPlayers={numPlayers}
                        teamInfo={teamInfo}
                        logoPreview={logoPreview}
                        setLogoFile={setLogoFile}
                        setLogoPreview={setLogoPreview}
                        logoPath={logoPath}
                        setLogoPath={setLogoPath}
                    />
                </div>
            </section>

            <CurrentSponsorsList age={age} sponsorships={sponsorships} jerseyStatus={jersey.status} />
        </div>
    );
}
